import React, { useState, useEffect } from 'react';
import { Upload, Eye, Edit2, Trash2, Image, X } from 'lucide-react';
import { galleryAPI, uploadAPI } from '../../../../services/dashboardAPI';

const GalleryManagement = ({ showNotification }) => {
  const [gallery, setGallery] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [filterCategory, setFilterCategory] = useState('all');
  const [editingId, setEditingId] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const categories = ['all', 'events', 'testimonials', 'team', 'gallery'];

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'events',
    imageUrl: '',
    tags: '',
  });

  // Fetch gallery on mount
  useEffect(() => {
    fetchGallery();
  }, [filterCategory]);

  const fetchGallery = async () => {
    try {
      setLoading(true);
      const filters = filterCategory !== 'all' ? { category: filterCategory } : {};
      const response = await galleryAPI.getAll(filters);
      if (response.success) {
        setGallery(response.data);
      } else {
        showNotification('Failed to fetch gallery', 'error');
      }
    } catch (error) {
      showNotification(error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadImage = async () => {
    if (!imageFile) return null;
    
    try {
      setUploading(true);
      const response = await uploadAPI.uploadFile(imageFile, 'gallery');
      if (response.success) {
        return response.data.url;
      } else {
        showNotification('Image upload failed', 'error');
        return null;
      }
    } catch (error) {
      showNotification(error.message, 'error');
      return null;
    } finally {
      setUploading(false);
    }
  };

  const handleSaveGallery = async () => {
    if (!formData.title || !formData.category) {
      showNotification('Title and category are required', 'error');
      return;
    }

    try {
      setLoading(true);
      let imageUrl = formData.imageUrl;
      
      if (imageFile) {
        imageUrl = await uploadImage();
        if (!imageUrl) {
          setLoading(false);
          return;
        }
      }

      const galleryPayload = {
        ...formData,
        imageUrl,
        tags: formData.tags ? formData.tags.split(',').map(t => t.trim()) : [],
      };

      let response;
      if (editingId) {
        response = await galleryAPI.update(editingId, galleryPayload);
      } else {
        response = await galleryAPI.create(galleryPayload);
      }

      if (response.success) {
        showNotification(
          editingId ? 'Gallery item updated!' : 'Gallery item added!'
        );
        setShowForm(false);
        setEditingId(null);
        setFormData({
          title: '',
          description: '',
          category: 'events',
          imageUrl: '',
          tags: '',
        });
        setImageFile(null);
        setImagePreview(null);
        fetchGallery();
      } else {
        showNotification('Failed to save gallery item', 'error');
      }
    } catch (error) {
      showNotification(error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (item) => {
    setFormData({
      ...item,
      tags: item.tags ? item.tags.join(', ') : '',
    });
    setImagePreview(item.imageUrl);
    setEditingId(item.id);
    setShowForm(true);
    setImageFile(null);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this gallery item?')) {
      try {
        setLoading(true);
        const response = await galleryAPI.delete(id);
        if (response.success) {
          showNotification('Gallery item deleted!');
          fetchGallery();
        } else {
          showNotification('Failed to delete item', 'error');
        }
      } catch (error) {
        showNotification(error.message, 'error');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingId(null);
    setFormData({
      title: '',
      description: '',
      category: 'events',
      imageUrl: '',
      tags: '',
    });
    setImageFile(null);
    setImagePreview(null);
  };

  const filteredGallery = filterCategory === 'all' ? gallery : gallery.filter(g => g.category === filterCategory);

  return (
    <>
      <div className="section-header">
        <div>
          <h2 className="title">Gallery Management</h2>
          <p className="subtitle">Manage images and media</p>
        </div>
        <button 
          className="btn-primary"
          onClick={() => setShowForm(!showForm)}
          disabled={loading}
        >
          <Upload size={18} /> Add Gallery Item
        </button>
      </div>

      <div className="filter-bar">
        {categories.map(cat => (
          <button
            key={cat}
            className={`filter-btn ${filterCategory === cat ? 'active' : ''}`}
            onClick={() => setFilterCategory(cat)}
          >
            {cat.charAt(0).toUpperCase() + cat.slice(1)}
          </button>
        ))}
      </div>

      {/* Add/Edit Form */}
      {showForm && (
        <div className="form-card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3>{editingId ? 'Edit Gallery Item' : 'Add Gallery Item'}</h3>
            <button onClick={handleCloseForm} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
              <X size={24} />
            </button>
          </div>

          <div className="form-group">
            <label>Title *</label>
            <input 
              type="text"
              placeholder="Enter title"
              value={formData.title}
              onChange={(e) => setFormData({...formData, title: e.target.value})}
            />
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea 
              placeholder="Enter description"
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              rows="3"
            />
          </div>

          <div className="form-group">
            <label>Category *</label>
            <select 
              value={formData.category}
              onChange={(e) => setFormData({...formData, category: e.target.value})}
            >
              <option value="events">Events</option>
              <option value="testimonials">Testimonials</option>
              <option value="team">Team</option>
              <option value="gallery">Gallery</option>
            </select>
          </div>

          <div className="form-group">
            <label>Tags (comma-separated)</label>
            <input 
              type="text"
              placeholder="e.g., networking, conference, 2024"
              value={formData.tags}
              onChange={(e) => setFormData({...formData, tags: e.target.value})}
            />
          </div>

          {/* Image Upload */}
          <div className="form-group">
            <label>Image *</label>
            <div style={{ marginBottom: '10px' }}>
              <input 
                type="file"
                accept="image/*"
                onChange={handleImageSelect}
                style={{ marginBottom: '10px' }}
              />
            </div>
            {imagePreview && (
              <div style={{ marginTop: '10px' }}>
                <img 
                  src={imagePreview} 
                  alt="Preview" 
                  style={{ maxWidth: '200px', maxHeight: '200px', borderRadius: '8px' }}
                />
                {imageFile && (
                  <button 
                    onClick={() => { setImageFile(null); setImagePreview(null); }}
                    style={{ marginLeft: '10px', background: '#dc3545', color: 'white', border: 'none', padding: '5px 10px', borderRadius: '4px', cursor: 'pointer' }}
                  >
                    Remove
                  </button>
                )}
              </div>
            )}
          </div>

          <div className="form-actions">
            <button className="btn-secondary" onClick={handleCloseForm} disabled={loading}>
              Cancel
            </button>
            <button 
              className="btn-primary" 
              onClick={handleSaveGallery}
              disabled={loading || uploading}
            >
              {loading || uploading ? 'Saving...' : editingId ? 'Update' : 'Add'}
            </button>
          </div>
        </div>
      )}

      {/* Gallery Grid */}
      {loading && !showForm ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>Loading...</div>
      ) : filteredGallery.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>No items found</div>
      ) : (
        <div className="gallery-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '20px', marginTop: '20px' }}>
          {filteredGallery.map(item => (
            <div key={item.id} className="gallery-item" style={{ border: '1px solid #ddd', borderRadius: '8px', overflow: 'hidden', backgroundColor: '#fff' }}>
              {item.imageUrl && (
                <img 
                  src={item.imageUrl} 
                  alt={item.title}
                  style={{ width: '100%', height: '180px', objectFit: 'cover' }}
                />
              )}
              <div style={{ padding: '15px' }}>
                <h4 style={{ margin: '0 0 10px 0' }}>{item.title}</h4>
                <p className="text-muted" style={{ fontSize: '12px', color: '#888', margin: '5px 0' }}>{item.category}</p>
                {item.views && <p className="text-small" style={{ fontSize: '12px', color: '#888', margin: '5px 0' }}>{item.views} views</p>}
                {item.tags && item.tags.length > 0 && (
                  <div style={{ marginTop: '10px', display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                    {item.tags.map((tag, idx) => (
                      <span key={idx} className="badge" style={{ fontSize: '11px', padding: '3px 8px' }}>{tag}</span>
                    ))}
                  </div>
                )}
                <div className="gallery-actions" style={{ display: 'flex', gap: '10px', marginTop: '15px', borderTop: '1px solid #eee', paddingTop: '10px' }}>
                  <button className="icon-btn" onClick={() => handleEdit(item)} disabled={loading} title="Edit">
                    <Edit2 size={16} />
                  </button>
                  <button className="icon-btn" onClick={() => handleDelete(item.id)} disabled={loading} title="Delete">
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
};

export default GalleryManagement;
